import {ReactNode} from 'react';
import {useNavigate} from '../../utils/hooks/use-navigate';
import {useSettings} from '../../core/settings/use-settings';
import {useAuth} from '../use-auth';
import {SocialService, useSocialLogin} from '../requests/use-social-login';
import {IconButton} from '../../ui/buttons/icon-button';
import {GoogleIcon} from '../../icons/social/google';
import {FacebookIcon} from '../../icons/social/facebook';
import {TwitterIcon} from '../../icons/social/twitter';
import {EnvatoIcon} from '../../icons/social/envato';
import {PhoneIcon} from '@common/icons/material/Phone';
import {EmailIcon} from '@common/icons/material/Email';

type Method = 'none' | 'phone' | 'email';

interface Props {
  // the method currently being filled; its own icon is not repeated here
  method: Method;
  onSelect: (method: Method) => void;
  label: ReactNode;
}

/**
 * Shown under an active login/signup form: a "back to all options" label plus
 * compact icons of the OTHER available methods, so the user can switch directly
 * without first returning to the full picker.
 */
export function OtherMethodsRow({method, onSelect, label}: Props) {
  const {social, mobile_login} = useSettings();
  const navigate = useNavigate();
  const {getRedirectUri} = useAuth();
  const {loginWithSocial} = useSocialLogin();

  const handleSocial = async (service: SocialService) => {
    const e = await loginWithSocial(service);
    if (e?.status === 'SUCCESS' || e?.status === 'ALREADY_LOGGED_IN') {
      navigate(getRedirectUri(), {replace: true});
    }
  };

  const icons: ReactNode[] = [];
  if (social?.google?.enable) {
    icons.push(
      <IconButton key="google" variant="outline" size="sm" onClick={() => handleSocial('google')}>
        <GoogleIcon viewBox="0 0 48 48" />
      </IconButton>,
    );
  }
  if (social?.facebook?.enable) {
    icons.push(
      <IconButton key="facebook" variant="outline" size="sm" onClick={() => handleSocial('facebook')}>
        <FacebookIcon className="text-facebook" />
      </IconButton>,
    );
  }
  if (social?.twitter?.enable) {
    icons.push(
      <IconButton key="twitter" variant="outline" size="sm" onClick={() => handleSocial('twitter')}>
        <TwitterIcon className="text-twitter" />
      </IconButton>,
    );
  }
  if (social?.envato?.enable) {
    icons.push(
      <IconButton key="envato" variant="outline" size="sm" onClick={() => handleSocial('envato')}>
        <EnvatoIcon viewBox="0 0 50 50" className="text-envato" />
      </IconButton>,
    );
  }
  if (mobile_login && method !== 'phone') {
    icons.push(
      <IconButton key="phone" variant="outline" size="sm" onClick={() => onSelect('phone')}>
        <PhoneIcon />
      </IconButton>,
    );
  }
  if (social?.email?.enable && method !== 'email') {
    icons.push(
      <IconButton key="email" variant="outline" size="sm" onClick={() => onSelect('email')}>
        <EmailIcon />
      </IconButton>,
    );
  }

  if (!icons.length) {
    return null;
  }

  return (
    <div className="mt-16 flex items-center justify-center gap-8">
      <button type="button" className="text-sm text-muted" onClick={() => onSelect('none')}>
        {label}
      </button>
      {icons}
    </div>
  );
}
